/** @jsx jsx */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useState } from 'react';
import { css, jsx } from '@emotion/core';
import { useLocation } from 'react-router-dom';
import { getBoard, getCommonSetting } from 'api/shiftBoard';
import { BoardData, PrintSetting } from 'domain/attend/shiftBoard/types';
import { resolvePrintRange, resolvePrintSetting } from 'domain/attend/shiftBoard/print';
import { makeTimeFormatter, resolveSexagesimal } from 'domain/attend/shiftBoard/time';
import PrintSheet from 'components/organismos/master/attend/ShiftBoard/PrintSheet';
import { boardStyles } from 'components/organismos/master/attend/ShiftBoard/styles';
import BoardSelect from 'components/organismos/master/attend/ShiftBoard/BoardSelect';

const styles = {
  page: css({
    background: '#fff',
    minHeight: '100vh',
    padding: '12px',
    '@media print': {
      padding: 0,
    },
  }),
  bar: css({
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    marginBottom: '8px',
    '@media print': { display: 'none' },
  }),
};

/**
 * 印刷ビュー（別タブで開く。サイドバーなし）
 * ルート: /shiftBoard/print?orgCode&dateFrom&dateTo
 */
const ShiftBoardPrintPage: React.FC = () => {
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const orgCode = query.get('orgCode') || '';
  const [setting, setSetting] = useState<PrintSetting>(() => {
    try {
      const s = sessionStorage.getItem('shiftBoard.printSetting');
      return resolvePrintSetting(s ? JSON.parse(s) : null);
    } catch (e) {
      return resolvePrintSetting(null);
    }
  });
  const [board, setBoard] = useState<BoardData | null>(null);
  // 時間の表記（9/16 決定）。共通設定が auto のときは勤怠の労務規約項目マスタに従う
  const [timeFormat, setTimeFormat] = useState<string>('auto');
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<{ from: string; to: string }>({ from: query.get('dateFrom') || '', to: query.get('dateTo') || '' });

  useEffect(() => {
    if (!orgCode || !range.from || !range.to) {
      return;
    }
    // 時間の表記と印刷の設定は自分用（9/23 に人単位へ移した）。無ければ店舗の値を使う
    const loginStaffCode = sessionStorage.getItem('loginUser.staffCode') || '';
    const load = async () => {
      let mine: { [key: string]: any } = {};
      try {
        mine = await getCommonSetting('shiftBoard', orgCode, loginStaffCode);
      } catch (e) {
        // 取れなくても店舗の値で印刷できる
      }
      try {
        const b = await getBoard(orgCode, range.from, range.to);
        setTimeFormat(typeof mine.timeFormat === 'string' ? mine.timeFormat : 'auto');
        setBoard(b);
        const saved = mine.printSetting || b.setting.printSetting;
        if (!sessionStorage.getItem('shiftBoard.printSetting') && saved) {
          setSetting(resolvePrintSetting(saved));
        }
      } catch (e) {
        setError((e.response && e.response.data && e.response.data.message) || '取得できませんでした。');
      }
    };
    load();
  }, [orgCode, range.from, range.to]);

  // 期間設定（半月／月）を反映
  useEffect(() => {
    if (!board) {
      return;
    }
    const r = resolvePrintRange(setting, { from: query.get('dateFrom') || '', to: query.get('dateTo') || '' }, board.org.monthStartDay);
    if (r.from !== range.from || r.to !== range.to) {
      setRange(r);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, setting.period]);

  const sexagesimal = resolveSexagesimal(timeFormat, board ? board.org.timeDisplayFormat : 1);
  const formatTime = makeTimeFormatter(sexagesimal, board ? board.org.roundMethodOfThirdDecimalPlace : 0);

  const margin = (on: boolean) => (on ? '10mm' : '0mm');
  const pageCss = `@page { size: ${setting.paper} ${setting.orientation === 'portrait' ? 'portrait' : 'landscape'}; `
    + `margin: ${margin(setting.marginTop)} ${margin(setting.marginRight)} 10mm ${margin(setting.marginLeft)}; }`;

  return (
    <div css={styles.page}>
      <style>{pageCss}</style>
      <div css={styles.bar}>
        <button type="button" css={boardStyles.iconButton} data-active="true" onClick={() => window.print()}>印刷</button>
        <button type="button" css={boardStyles.iconButton} onClick={() => window.close()}>閉じる</button>
        <span css={boardStyles.subText}>{`${setting.paper} ${setting.orientation === 'portrait' ? '縦' : '横'} / ${setting.fontSize === 'large' ? '大きめ' : setting.fontSize === 'medium' ? '標準' : '小さめ'}${setting.monochrome ? ' / モノクロ' : ''}`}</span>
        {/* ほかの画面と同じ共通の Select（10/7） */}
        <span css={boardStyles.subText} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          用紙
          <BoardSelect
            id="pp-paper"
            label="用紙"
            value={setting.paper}
            onChange={(v) => setSetting({ ...setting, paper: v as PrintSetting['paper'] })}
            options={[{ value: 'A4', label: 'A4' }, { value: 'A3', label: 'A3' }]}
            style={{ width: '90px' }}
          />
        </span>
        <span css={boardStyles.subText} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          文字
          <BoardSelect
            id="pp-font"
            label="文字"
            value={setting.fontSize}
            onChange={(v) => setSetting({ ...setting, fontSize: v as PrintSetting['fontSize'] })}
            options={[{ value: 'large', label: '大' }, { value: 'medium', label: '中' }, { value: 'small', label: '小' }]}
            style={{ width: '80px' }}
          />
        </span>
      </div>
      {error && <div css={boardStyles.dangerText}>{error}</div>}
      {!board && !error && <div css={boardStyles.subText}>読み込み中…</div>}
      {board && (
        <PrintSheet board={board} setting={setting} dateFrom={range.from} dateTo={range.to} formatTime={formatTime} />
      )}
    </div>
  );
};

export default ShiftBoardPrintPage;
